// src/pages/forge/SquadRegistration.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/lib/supabaseClient';
import { 
  Users, 
  Award, 
  ArrowLeft, 
  Copy, 
  Check, 
  CheckCircle2, 
  Loader2, 
  AlertCircle,
  UserCheck,
  Sparkles,
  RefreshCw,
  LogOut,
  Home,
  Save,
  Building2,
  Lightbulb,
  Wrench,
  Heart,
  TrendingUp,
  Clock,
  Target,
  Globe,
  Briefcase,
  Palette,
  ShoppingBag,
  GraduationCap,
  Plus,
  X,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { 
  IndustrySector, 
  SubSector, 
  ProblemCategory, 
  TechnicalArea, 
  ImpactArea,
  CompanyStage,
  EngagementPreference,
  InterestTemplates,
  TeamSize,
  ExperienceLevel,
  getProblemMatchScore
} from '@/types/forge';

type RegistrationStep = 'squad-info' | 'business-interests' | 'invite' | 'lead-assessment' | 'member-role-select' | 'member-assessment' | 'review';
type RegistrationMode = 'lead' | 'member';

// Business Interest Options
const industrySectorOptions: { id: IndustrySector; label: string; description: string; icon: any }[] = [
  { id: 'agribusiness', label: 'Agribusiness', description: 'Farming, food processing, agricultural inputs', icon: Building2 },
  { id: 'manufacturing', label: 'Manufacturing', description: 'FMCG, automotive, textiles, plastics', icon: Wrench },
  { id: 'logistics_transport', label: 'Logistics & Transport', description: 'Freight, cold chain, last-mile delivery', icon: TrendingUp },
  { id: 'technology_software', label: 'Technology & Software', description: 'SaaS, fintech, agritech, healthtech', icon: Lightbulb },
  { id: 'healthcare_pharma', label: 'Healthcare & Pharma', description: 'Hospitals, medical devices, pharma', icon: Heart },
  { id: 'energy_utilities', label: 'Energy & Utilities', description: 'Renewable energy, power, water, waste', icon: Clock },
  { id: 'retail_consumer', label: 'Retail & Consumer', description: 'E-commerce, retail chains, consumer goods', icon: ShoppingBag },
  { id: 'financial_services', label: 'Financial Services', description: 'Banking, insurance, microfinance', icon: Briefcase },
  { id: 'education_training', label: 'Education & Training', description: 'EdTech, vocational training, higher education', icon: GraduationCap }
];

const subSectorOptions: { id: SubSector; label: string; sector: IndustrySector }[] = [
  { id: 'crop_farming', label: 'Crop Farming', sector: 'agribusiness' },
  { id: 'livestock_poultry', label: 'Livestock & Poultry', sector: 'agribusiness' },
  { id: 'food_processing', label: 'Food Processing', sector: 'agribusiness' },
  { id: 'agricultural_inputs', label: 'Agricultural Inputs', sector: 'agribusiness' },
  { id: 'agritech', label: 'AgriTech', sector: 'agribusiness' },
  { id: 'fmcg_manufacturing', label: 'FMCG Manufacturing', sector: 'manufacturing' },
  { id: 'automotive_assembly', label: 'Automotive Assembly', sector: 'manufacturing' },
  { id: 'textile_apparel', label: 'Textile & Apparel', sector: 'manufacturing' },
  { id: 'plastics_packaging', label: 'Plastics & Packaging', sector: 'manufacturing' },
  { id: 'electronics_assembly', label: 'Electronics Assembly', sector: 'manufacturing' },
  { id: 'saas', label: 'SaaS', sector: 'technology_software' },
  { id: 'fintech', label: 'FinTech', sector: 'technology_software' },
  { id: 'agritech', label: 'AgriTech', sector: 'technology_software' },
  { id: 'healthtech', label: 'HealthTech', sector: 'technology_software' },
  { id: 'edtech', label: 'EdTech', sector: 'technology_software' },
  { id: 'renewable_energy', label: 'Renewable Energy', sector: 'energy_utilities' },
  { id: 'energy_efficiency', label: 'Energy Efficiency', sector: 'energy_utilities' },
  { id: 'waste_management', label: 'Waste Management', sector: 'energy_utilities' },
  { id: 'water_utilities', label: 'Water Utilities', sector: 'energy_utilities' },
  { id: 'hospital_clinic', label: 'Hospital & Clinic', sector: 'healthcare_pharma' },
  { id: 'medical_devices', label: 'Medical Devices', sector: 'healthcare_pharma' },
  { id: 'pharma_distribution', label: 'Pharma Distribution', sector: 'healthcare_pharma' },
  { id: 'healthcare_tech', label: 'Healthcare Tech', sector: 'healthcare_pharma' },
  { id: 'retail_chain', label: 'Retail Chain', sector: 'retail_consumer' },
  { id: 'e_commerce', label: 'E-Commerce', sector: 'retail_consumer' },
  { id: 'wholesale_distribution', label: 'Wholesale Distribution', sector: 'retail_consumer' },
  { id: 'freight_logistics', label: 'Freight Logistics', sector: 'logistics_transport' },
  { id: 'cold_chain', label: 'Cold Chain', sector: 'logistics_transport' },
  { id: 'last_mile_delivery', label: 'Last Mile Delivery', sector: 'logistics_transport' },
  { id: 'warehousing_storage', label: 'Warehousing & Storage', sector: 'logistics_transport' }
];

const problemCategoryOptions: { id: ProblemCategory; label: string; group: string }[] = [
  { id: 'production_downtime', label: 'Production Downtime', group: 'Operational Efficiency' },
  { id: 'quality_defects', label: 'Quality Defects', group: 'Operational Efficiency' },
  { id: 'supply_chain_disruption', label: 'Supply Chain Disruption', group: 'Operational Efficiency' },
  { id: 'inventory_management', label: 'Inventory Management', group: 'Operational Efficiency' },
  { id: 'maintenance_issues', label: 'Maintenance Issues', group: 'Operational Efficiency' },
  { id: 'labor_inefficiency', label: 'Labor Inefficiency', group: 'Operational Efficiency' },
  { id: 'capacity_utilization', label: 'Capacity Utilization', group: 'Operational Efficiency' },
  { id: 'energy_waste', label: 'Energy Waste', group: 'Cost Reduction' },
  { id: 'material_waste', label: 'Material Waste', group: 'Cost Reduction' },
  { id: 'labor_cost_overrun', label: 'Labor Cost Overrun', group: 'Cost Reduction' },
  { id: 'logistics_cost', label: 'Logistics Cost', group: 'Cost Reduction' },
  { id: 'procurement_inefficiency', label: 'Procurement Inefficiency', group: 'Cost Reduction' },
  { id: 'customer_acquisition', label: 'Customer Acquisition', group: 'Revenue Growth' },
  { id: 'customer_retention', label: 'Customer Retention', group: 'Revenue Growth' },
  { id: 'pricing_strategy', label: 'Pricing Strategy', group: 'Revenue Growth' },
  { id: 'market_expansion', label: 'Market Expansion', group: 'Revenue Growth' },
  { id: 'product_innovation', label: 'Product Innovation', group: 'Revenue Growth' },
  { id: 'digital_transformation', label: 'Digital Transformation', group: 'Technology & Digital' },
  { id: 'automation_opportunity', label: 'Automation Opportunity', group: 'Technology & Digital' },
  { id: 'data_analytics', label: 'Data Analytics', group: 'Technology & Digital' },
  { id: 'legacy_systems', label: 'Legacy Systems', group: 'Technology & Digital' },
  { id: 'cybersecurity', label: 'Cybersecurity', group: 'Technology & Digital' },
  { id: 'carbon_emissions', label: 'Carbon Emissions Reduction', group: 'Sustainability & ESG' },
  { id: 'water_conservation', label: 'Water Conservation', group: 'Sustainability & ESG' },
  { id: 'waste_reduction', label: 'Waste Reduction', group: 'Sustainability & ESG' },
  { id: 'circular_economy', label: 'Circular Economy', group: 'Sustainability & ESG' },
  { id: 'sustainable_sourcing', label: 'Sustainable Sourcing', group: 'Sustainability & ESG' },
  { id: 'working_capital', label: 'Working Capital Management', group: 'Financial & Business' },
  { id: 'cash_flow', label: 'Cash Flow Management', group: 'Financial & Business' },
  { id: 'profitability', label: 'Profitability Improvement', group: 'Financial & Business' },
  { id: 'business_model', label: 'Business Model Innovation', group: 'Financial & Business' },
  { id: 'market_access', label: 'Market Access', group: 'Financial & Business' }
];

const technicalAreaOptions: { id: TechnicalArea; label: string }[] = [
  { id: 'data_analytics', label: 'Data Analytics' },
  { id: 'software_development', label: 'Software Development' },
  { id: 'hardware_engineering', label: 'Hardware Engineering' },
  { id: 'mechanical_engineering', label: 'Mechanical Engineering' },
  { id: 'electrical_engineering', label: 'Electrical Engineering' },
  { id: 'chemical_engineering', label: 'Chemical Engineering' },
  { id: 'industrial_design', label: 'Industrial Design' },
  { id: 'business_analytics', label: 'Business Analytics' },
  { id: 'financial_modeling', label: 'Financial Modeling' },
  { id: 'market_research', label: 'Market Research' },
  { id: 'user_experience', label: 'User Experience (UX)' },
  { id: 'project_management', label: 'Project Management' },
  { id: 'supply_chain', label: 'Supply Chain Management' },
  { id: 'operations_management', label: 'Operations Management' },
  { id: 'sales_marketing', label: 'Sales & Marketing' },
  { id: 'human_resources', label: 'Human Resources' }
];

const impactAreaOptions: { id: ImpactArea; label: string }[] = [
  { id: 'climate_change', label: 'Climate Change Mitigation' },
  { id: 'food_security', label: 'Food Security' },
  { id: 'clean_energy', label: 'Clean Energy Access' },
  { id: 'water_access', label: 'Water Access & Conservation' },
  { id: 'healthcare_access', label: 'Healthcare Access' },
  { id: 'education_access', label: 'Education Access' },
  { id: 'financial_inclusion', label: 'Financial Inclusion' },
  { id: 'gender_equality', label: 'Gender Equality' },
  { id: 'job_creation', label: 'Job Creation' },
  { id: 'rural_development', label: 'Rural Development' },
  { id: 'circular_economy', label: 'Circular Economy' },
  { id: 'sustainable_agriculture', label: 'Sustainable Agriculture' }
];

const companyStageOptions: { id: CompanyStage; label: string }[] = [
  { id: 'early_stage_startup', label: 'Early Stage Startup' },
  { id: 'growth_stage', label: 'Growth Stage' },
  { id: 'established_sme', label: 'Established SME' },
  { id: 'corporate', label: 'Corporate' },
  { id: 'family_business', label: 'Family Business' },
  { id: 'social_enterprise', label: 'Social Enterprise' }
];

const engagementOptions: { id: EngagementPreference; label: string }[] = [
  { id: 'hands_on_implementation', label: 'Hands-on Implementation' },
  { id: 'strategy_consulting', label: 'Strategy Consulting' },
  { id: 'research_analysis', label: 'Research & Analysis' },
  { id: 'product_development', label: 'Product Development' },
  { id: 'market_entry', label: 'Market Entry Strategy' },
  { id: 'process_optimization', label: 'Process Optimization' },
  { id: 'technology_adoption', label: 'Technology Adoption' },
  { id: 'business_model_design', label: 'Business Model Design' },
  { id: 'impact_measurement', label: 'Impact Measurement' }
];

const teamSizeOptions: TeamSize[] = ['2-3', '4-5', '6-8'];
const experienceLevelOptions: ExperienceLevel[] = ['beginner', 'intermediate', 'advanced'];

// Default roles (required, cannot be removed)
const DEFAULT_ROLES = [
  { id: 'market_analyst', name: 'Market Analyst' },
  { id: 'product_lead', name: 'Product Lead' },
  { id: 'finance_lead', name: 'Finance Lead' }
];

// ==============================================
// ASSESSMENT QUESTIONS
// ==============================================

// Base questions for all members (Page 1 - 5 questions)
const baseQuestions = [
  { id: 'motivation', text: 'Why do you want to join The Forge? What specific problem do you want to solve?' },
  { id: 'commitment', text: 'How many hours per week can you consistently dedicate to your squad?' },
  { id: 'team_experience', text: 'Describe a time you worked in a high-pressure team. What was your role and how did you handle it?' },
  { id: 'skills_contribution', text: 'What specific skills, knowledge, or experience will you bring to your squad?' },
  { id: 'learning_goals', text: 'What do you hope to learn or gain from this experience?' }
];

// Role-specific questions (Page 2 - 5 questions each)
const roleSpecificQuestions = {
  lead: [
    { id: 'lead_experience', text: 'Have you led a team or project before? Describe your leadership experience and style.' },
    { id: 'conflict_resolution', text: 'How do you handle disagreements or conflicts within a team? Give a specific example.' },
    { id: 'vision', text: 'What is your vision for your squad? What kind of venture do you want to build?' },
    { id: 'decision_making', text: 'How do you make important decisions when there are competing priorities?' },
    { id: 'motivation_style', text: 'How do you motivate team members when facing challenges or setbacks?' }
  ],
  market_analyst: [
    { id: 'research_experience', text: 'Describe your experience with market research, data analysis, or customer interviews.' },
    { id: 'industry_knowledge', text: 'Which industries or markets are you most passionate about and why?' },
    { id: 'analytical_skills', text: 'How do you validate if a problem is worth solving? Walk us through your process.' },
    { id: 'competitor_analysis', text: 'How do you analyze competitors and identify market opportunities?' },
    { id: 'data_tools', text: 'What tools or methods do you use for data analysis and market research?' }
  ],
  product_lead: [
    { id: 'product_experience', text: 'Have you built or designed a product before? Tell us about it and what you learned.' },
    { id: 'technical_skills', text: 'What technical, design, or prototyping skills do you bring to the team?' },
    { id: 'user_focus', text: 'How do you ensure you\'re building something users actually want?' },
    { id: 'feature_prioritization', text: 'How do you prioritize features when resources are limited?' },
    { id: 'product_metrics', text: 'What metrics do you use to measure product success?' }
  ],
  finance_lead: [
    { id: 'finance_experience', text: 'Describe your experience with budgets, financial planning, or business models.' },
    { id: 'numeracy', text: 'How comfortable are you with financial projections, unit economics, and numbers? (1-5 scale)' },
    { id: 'business_acumen', text: 'What makes a business financially sustainable? Share your thoughts.' },
    { id: 'financial_modeling', text: 'Have you built financial models before? What factors do you consider?' },
    { id: 'fundraising', text: 'What do you know about fundraising, investment, or venture capital?' }
  ]
};

// Custom role questions (5 questions for Page 2)
const customRoleQuestions = [
  { id: 'problem_solving', text: 'Describe a complex problem you solved. What was your approach and what was the outcome?' },
  { id: 'communication', text: 'How do you communicate complex ideas to different audiences?' },
  { id: 'adaptability', text: 'Tell us about a time you had to adapt to unexpected changes. How did you handle it?' },
  { id: 'leadership_potential', text: 'Have you taken initiative in a team setting? Describe the situation and your role.' },
  { id: 'growth_mindset', text: 'How do you handle feedback and criticism? Give an example of how you used feedback to improve.' }
];

// Get role-specific questions for a given role
const getRoleSpecificQuestions = (roleId: string) => {
  if (roleId === 'lead') return roleSpecificQuestions.lead;
  if (roleId === 'market_analyst') return roleSpecificQuestions.market_analyst;
  if (roleId === 'product_lead') return roleSpecificQuestions.product_lead;
  if (roleId === 'finance_lead') return roleSpecificQuestions.finance_lead;
  return customRoleQuestions; // For custom roles
};

// Storage keys
const STORAGE_KEYS = {
  LEAD_SQUAD_ID: 'squad_registration_id',
  LEAD_SQUAD_INFO: 'squad_info',
  LEAD_INTERESTS: 'squad_interests',
  LEAD_ASSESSMENT: 'lead_assessment',
  LEAD_INVITE_LINK: 'lead_invite_link',
  CUSTOM_ROLES: 'custom_roles',
  MEMBER_ROLE: 'member_role',
  MEMBER_ASSESSMENT: 'member_assessment',
  MEMBER_INFO: 'member_info',
  MEMBER_SESSION: (inviteCode: string) => `member_session_${inviteCode}`
};

export default function SquadRegistration() {
  const navigate = useNavigate();
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState<RegistrationStep>('squad-info');
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode>('lead');
  const [squadId, setSquadId] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState(20);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [hasExistingSquad, setHasExistingSquad] = useState(false);
  const [existingMembers, setExistingMembers] = useState<any[]>([]);
  const [leadAssessmentCompleted, setLeadAssessmentCompleted] = useState(false);
  
  const [squadInfo, setSquadInfo] = useState({
    squadName: '',
    university: '',
    leadName: '',
    leadEmail: '',
    leadPhone: '',
    teamSize: '4-5' as TeamSize,
    experienceLevel: 'intermediate' as ExperienceLevel
  });

  // Custom Roles State
  const [customRoles, setCustomRoles] = useState<{ id: string; name: string }[]>([]);
  const [newRoleName, setNewRoleName] = useState('');

  // Business Interests
  const [businessInterests, setBusinessInterests] = useState({
    primarySectors: [] as IndustrySector[],
    secondarySectors: [] as IndustrySector[],
    preferredSubSectors: [] as SubSector[],
    problemCategories: [] as ProblemCategory[],
    technicalAreas: [] as TechnicalArea[],
    impactAreas: [] as ImpactArea[],
    companyStagePreferences: [] as CompanyStage[],
    engagementPreferences: [] as EngagementPreference[]
  });
  
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [lookingFor, setLookingFor] = useState<string[]>([]);

  // Lead's own assessment
  const [leadAssessment, setLeadAssessment] = useState<Record<string, string>>({});
  
  // Member info for team member registration
  const [memberInfo, setMemberInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: '',
    yearOfStudy: '',
    course: ''
  });
  
  const [memberAssessment, setMemberAssessment] = useState<Record<string, string>>({});
  const [assessmentPage, setAssessmentPage] = useState(1);

  // Add custom role
  const addCustomRole = () => {
    if (!newRoleName.trim()) {
      toast({
        title: 'Role Name Required',
        description: 'Please enter a role name.',
        variant: 'destructive'
      });
      return;
    }
    
    const newRole = {
      id: `custom_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      name: newRoleName.trim()
    };
    
    setCustomRoles([...customRoles, newRole]);
    setNewRoleName('');
  };

  // Remove custom role
  const removeCustomRole = (roleId: string) => {
    setCustomRoles(customRoles.filter(role => role.id !== roleId));
  };

  // Get all roles (default + custom) - EXCLUDING lead
  const getAllRoles = () => {
    return [...DEFAULT_ROLES, ...customRoles];
  };

  // Calculate max members based on team size
  const getMaxMembersFromTeamSize = (teamSize: TeamSize) => {
    switch (teamSize) {
      case '2-3': return 3;
      case '4-5': return 5;
      case '6-8': return 8;
      default: return 5;
    }
  };

  const getMaxTeamMembersFromTeamSize = (teamSize: TeamSize) => {
    switch (teamSize) {
      case '2-3': return 2;
      case '4-5': return 4;
      case '6-8': return 7;
      default: return 4;
    }
  };

  // Apply template function
  const applyTemplate = (templateName: string) => {
    const template = InterestTemplates[templateName as keyof typeof InterestTemplates];
    if (template) {
      setBusinessInterests({
        ...businessInterests,
        primarySectors: template.primary_sectors,
        preferredSubSectors: template.preferred_sub_sectors,
        problemCategories: template.problem_categories,
        technicalAreas: template.technical_areas,
        impactAreas: template.impact_areas,
        companyStagePreferences: template.company_stage_preferences,
        engagementPreferences: template.engagement_preferences
      });
      setSelectedTemplate(templateName);
      toast({
        title: "Template Applied",
        description: `${templateName.replace('_', ' ')} interests loaded. You can customize further.`,
      });
    }
  };

  // Save business interests to localStorage
  const saveBusinessInterests = () => {
    localStorage.setItem(STORAGE_KEYS.LEAD_INTERESTS, JSON.stringify(businessInterests));
  };

  // Save custom roles to localStorage
  const saveCustomRoles = () => {
    localStorage.setItem(STORAGE_KEYS.CUSTOM_ROLES, JSON.stringify(customRoles));
  };

  // Auto-save business interests
  useEffect(() => {
    if (registrationMode === 'lead' && currentStep === 'business-interests') {
      const timer = setTimeout(() => saveBusinessInterests(), 1000);
      return () => clearTimeout(timer);
    }
  }, [businessInterests, registrationMode, currentStep]);

  // Auto-save custom roles
  useEffect(() => {
    if (registrationMode === 'lead' && currentStep === 'squad-info') {
      const timer = setTimeout(() => saveCustomRoles(), 1000);
      return () => clearTimeout(timer);
    }
  }, [customRoles, registrationMode, currentStep]);

  // Load saved interests on mount
  useEffect(() => {
    if (registrationMode === 'lead') {
      const savedInterests = localStorage.getItem(STORAGE_KEYS.LEAD_INTERESTS);
      if (savedInterests) {
        try {
          setBusinessInterests(JSON.parse(savedInterests));
        } catch (e) {
          console.error('Error loading saved interests:', e);
        }
      }
      
      const savedCustomRoles = localStorage.getItem(STORAGE_KEYS.CUSTOM_ROLES);
      if (savedCustomRoles) {
        try {
          setCustomRoles(JSON.parse(savedCustomRoles));
        } catch (e) {
          console.error('Error loading custom roles:', e);
        }
      }
    }
  }, [registrationMode]);

  // Check for existing squad on load
  useEffect(() => {
    const savedSquadId = localStorage.getItem(STORAGE_KEYS.LEAD_SQUAD_ID);
    const savedSquadInfo = localStorage.getItem(STORAGE_KEYS.LEAD_SQUAD_INFO);
    const savedInterests = localStorage.getItem(STORAGE_KEYS.LEAD_INTERESTS);
    const savedAssessment = localStorage.getItem(STORAGE_KEYS.LEAD_ASSESSMENT);
    const savedInviteLink = localStorage.getItem(STORAGE_KEYS.LEAD_INVITE_LINK);
    const savedCustomRoles = localStorage.getItem(STORAGE_KEYS.CUSTOM_ROLES);
    
    if (savedSquadId && savedSquadInfo && !inviteCode) {
      setHasExistingSquad(true);
      setSquadId(savedSquadId);
      setSquadInfo(JSON.parse(savedSquadInfo));
      if (savedInterests) setBusinessInterests(JSON.parse(savedInterests));
      if (savedAssessment) setLeadAssessment(JSON.parse(savedAssessment));
      if (savedInviteLink) setInviteLink(savedInviteLink);
      if (savedCustomRoles) setCustomRoles(JSON.parse(savedCustomRoles));
      setCurrentStep('review');
      setRegistrationMode('lead');
    }
  }, [inviteCode]);

  // Check invite
  useEffect(() => {
    if (inviteCode && inviteCode !== 'undefined') {
      setRegistrationMode('member');
      setCurrentStep('member-role-select');
      loadSquadFromInvite(inviteCode);
    }
  }, [inviteCode]);

  const loadSquadFromInvite = async (code: string) => {
    try {
      setLoading(true);
      
      const { data: invite, error: inviteError } = await supabase
        .from('squad_invites')
        .select(`
          *,
          squad_registrations!squad_invites_registration_id_fkey (*)
        `)
        .eq('invite_code', code)
        .single();

      if (inviteError || !invite) {
        toast({
          title: 'Invalid Invite',
          description: 'This invite link is invalid or has expired.',
          variant: 'destructive'
        });
        setInviteValid(false);
        setTimeout(() => navigate('/forge'), 2000);
        return;
      }

      if (invite.used_count >= invite.max_uses) {
        toast({
          title: 'Invite Used',
          description: 'This invite link has reached its maximum uses.',
          variant: 'destructive'
        });
        setInviteValid(false);
        setTimeout(() => navigate('/forge'), 2000);
        return;
      }

      if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
        toast({
          title: 'Invite Expired',
          description: 'This invite link has expired.',
          variant: 'destructive'
        });
        setInviteValid(false);
        setTimeout(() => navigate('/forge'), 2000);
        return;
      }

      const { data: members } = await supabase.from('squad_members').select('*').eq('squad_id', invite.squad_id);
      const currentCount = members?.length || 0;
      
      const squadData = invite.squad_registrations;
      const maxMembers = squadData?.max_members || 5;
      
      if (currentCount >= maxMembers) {
        toast({
          title: 'Squad Full',
          description: `This squad has reached its maximum of ${maxMembers} members.`,
          variant: 'destructive'
        });
        setInviteValid(false);
        setTimeout(() => navigate('/forge'), 2000);
        return;
      }

      setSquadId(invite.squad_id);
      setSquadInfo({
        ...squadInfo,
        squadName: squadData?.squad_name || '',
        university: squadData?.university || ''
      });
      
      // Load custom roles for this squad
      if (squadData?.custom_roles) {
        try {
          const loadedRoles = JSON.parse(squadData.custom_roles);
          if (Array.isArray(loadedRoles)) {
            setCustomRoles(loadedRoles);
          }
        } catch (e) {
          console.error('Error parsing custom roles:', e);
        }
      }
      
      setInviteValid(true);
      setExistingMembers(members || []);
      
      // Load saved member data if exists
      const savedRole = localStorage.getItem(STORAGE_KEYS.MEMBER_ROLE);
      const savedInfo = localStorage.getItem(STORAGE_KEYS.MEMBER_INFO);
      const savedAssessment = localStorage.getItem(STORAGE_KEYS.MEMBER_ASSESSMENT);
      
      if (savedRole) setMemberInfo(prev => ({ ...prev, role: savedRole }));
      if (savedInfo) {
        try {
          const info = JSON.parse(savedInfo);
          setMemberInfo(prev => ({ ...prev, ...info }));
        } catch (e) {}
      }
      if (savedAssessment) {
        try {
          setMemberAssessment(JSON.parse(savedAssessment));
        } catch (e) {}
      }
      
      toast({
        title: 'Welcome!',
        description: `You've been invited to join ${squadData?.squad_name}. ${maxMembers - currentCount} spots remaining.`,
      });
    } catch (error) {
      console.error('Error loading invite:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invite. Please try again.',
        variant: 'destructive'
      });
      setInviteValid(false);
    } finally {
      setLoading(false);
    }
  };

  const loadSquadMembers = async (id?: string) => {
    const targetId = id || squadId;
    if (!targetId) return;
    try {
      const { data: members } = await supabase.from('squad_members').select('*').eq('squad_id', targetId);
      setExistingMembers(members || []);
      const lead = members?.find(m => m.role === 'lead');
      setLeadAssessmentCompleted(lead?.assessment_completed || false);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  useEffect(() => {
    if (squadId && registrationMode === 'lead') {
      loadSquadMembers();
      const interval = setInterval(() => loadSquadMembers(), 5000);
      return () => clearInterval(interval);
    }
  }, [squadId]);

  useEffect(() => {
    const stepProgress: Record<RegistrationStep, number> = {
      'squad-info': 20,
      'business-interests': 40,
      'invite': 60,
      'lead-assessment': 75,
      'member-role-select': 30,
      'member-assessment': 60,
      'review': 90
    };
    setProgress(stepProgress[currentStep]);
  }, [currentStep]);

  const generateInviteCode = () => Math.random().toString(36).substring(2, 10).toUpperCase();

  const createSquad = async () => {
    setLoading(true);
    try {
      const inviteCode = generateInviteCode();
      const allRoles = getAllRoles();
      
      const maxTeamMembers = getMaxTeamMembersFromTeamSize(squadInfo.teamSize);
      const maxMembers = getMaxMembersFromTeamSize(squadInfo.teamSize);
      
      const { data: squad, error: squadError } = await supabase
        .from('squad_registrations')
        .insert({
          squad_code: `SQD-${Date.now()}`,
          squad_name: squadInfo.squadName,
          university: squadInfo.university,
          invite_code: inviteCode,
          team_size: squadInfo.teamSize,
          experience_level: squadInfo.experienceLevel,
          business_interests: businessInterests,
          looking_for: lookingFor,
          custom_roles: JSON.stringify(allRoles),
          max_members: maxMembers,
          current_members: 0,
          status: 'pending'
        })
        .select()
        .single();

      if (squadError) throw squadError;
      setSquadId(squad.id);

      await supabase.from('squad_invites').insert({
        invite_code: inviteCode,
        squad_id: squad.id,
        max_uses: maxTeamMembers,
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      });

      const link = `${window.location.origin}/app/forge/register/${inviteCode}`;
      setInviteLink(link);

      localStorage.setItem(STORAGE_KEYS.LEAD_SQUAD_ID, squad.id);
      localStorage.setItem(STORAGE_KEYS.LEAD_SQUAD_INFO, JSON.stringify(squadInfo));
      localStorage.setItem(STORAGE_KEYS.LEAD_INTERESTS, JSON.stringify(businessInterests));
      localStorage.setItem(STORAGE_KEYS.LEAD_INVITE_LINK, link);
      localStorage.setItem(STORAGE_KEYS.CUSTOM_ROLES, JSON.stringify(customRoles));

      setCurrentStep('business-interests');
      toast({ title: 'Squad Created!', description: 'Now tell us about your squad\'s interests.' });
    } catch (error) {
      console.error('Error creating squad:', error);
      toast({ title: 'Error', description: 'Failed to create squad.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const submitLeadAssessment = async () => {
    const allBaseAnswered = baseQuestions.every(q => leadAssessment[q.id] && leadAssessment[q.id].trim() !== '');
    const roleQuestions = getRoleSpecificQuestions('lead');
    const allRoleAnswered = roleQuestions.every(q => leadAssessment[q.id] && leadAssessment[q.id].trim() !== '');
    const allAnswered = allBaseAnswered && allRoleAnswered;
    
    if (!allAnswered) {
      setError('Please complete all assessment questions.');
      return;
    }

    setLoading(true);
    try {
      localStorage.setItem(STORAGE_KEYS.LEAD_ASSESSMENT, JSON.stringify(leadAssessment));

      const { data: existingLead } = await supabase
        .from('squad_members')
        .select('*')
        .eq('squad_id', squadId)
        .eq('email', squadInfo.leadEmail)
        .single();

      if (existingLead) {
        await supabase.from('squad_members').update({
          assessment_responses: leadAssessment,
          assessment_completed: true
        }).eq('id', existingLead.id);
      } else {
        await supabase.from('squad_members').insert({
          squad_id: squadId,
          full_name: squadInfo.leadName,
          email: squadInfo.leadEmail,
          phone: squadInfo.leadPhone,
          role: 'lead',
          year_of_study: 'N/A',
          course: 'N/A',
          assessment_responses: leadAssessment,
          assessment_completed: true
        });
      }

      await supabase.from('squad_registrations').update({ current_members: 1 }).eq('id', squadId);
      setLeadAssessmentCompleted(true);
      setCurrentStep('review');
      toast({ title: 'Assessment Complete!', description: 'Your assessment has been saved.' });
    } catch (error) {
      console.error('Error submitting assessment:', error);
      toast({ title: 'Error', description: 'Failed to submit assessment.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleMemberInfoSubmit = () => {
    if (!memberInfo.fullName || !memberInfo.email || !memberInfo.role) {
      setError('Please fill in all required fields.');
      return;
    }
    
    if (!memberInfo.yearOfStudy || !memberInfo.course) {
      setError('Please fill in your year of study and course.');
      return;
    }
    
    // Save member info
    localStorage.setItem(STORAGE_KEYS.MEMBER_ROLE, memberInfo.role);
    localStorage.setItem(STORAGE_KEYS.MEMBER_INFO, JSON.stringify({
      fullName: memberInfo.fullName,
      email: memberInfo.email,
      phone: memberInfo.phone,
      yearOfStudy: memberInfo.yearOfStudy,
      course: memberInfo.course
    }));
    
    // Reset assessment page and go to assessment
    setAssessmentPage(1);
    setCurrentStep('member-assessment');
  };

  const isPageComplete = () => {
    const currentQuestions = assessmentPage === 1 ? baseQuestions : getRoleSpecificQuestions(memberInfo.role);
    return currentQuestions.every(q => 
      memberAssessment[q.id] && memberAssessment[q.id].trim() !== ''
    );
  };

  const handleNextPage = () => {
    if (!isPageComplete()) {
      setError('Please answer all questions before continuing.');
      return;
    }
    setError(null);
    setAssessmentPage(2);
  };

  const handleSubmitAssessment = async () => {
    if (!isPageComplete()) {
      setError('Please answer all questions before submitting.');
      return;
    }
    await submitMemberRegistration();
  };

  const submitMemberRegistration = async () => {
    setLoading(true);
    try {
      const { data: existing } = await supabase
        .from('squad_members')
        .select('*')
        .eq('squad_id', squadId)
        .eq('email', memberInfo.email)
        .single();

      if (existing) {
        toast({ title: 'Already Registered', description: 'This email is already registered.', variant: 'destructive' });
        setLoading(false);
        return;
      }

      const { data: members } = await supabase.from('squad_members').select('id').eq('squad_id', squadId);
      const currentCount = members?.length || 0;
      
      const { data: squad } = await supabase
        .from('squad_registrations')
        .select('max_members')
        .eq('id', squadId)
        .single();
      
      const maxMembers = squad?.max_members || 5;
      
      if (currentCount >= maxMembers) {
        toast({ title: 'Squad Full', description: `This squad has reached its maximum of ${maxMembers} members.`, variant: 'destructive' });
        navigate('/forge');
        setLoading(false);
        return;
      }

      // Save assessment to localStorage
      localStorage.setItem(STORAGE_KEYS.MEMBER_ASSESSMENT, JSON.stringify(memberAssessment));

      await supabase.from('squad_members').insert({
        squad_id: squadId,
        full_name: memberInfo.fullName,
        email: memberInfo.email,
        phone: memberInfo.phone,
        role: memberInfo.role,
        year_of_study: memberInfo.yearOfStudy,
        course: memberInfo.course,
        assessment_responses: memberAssessment,
        assessment_completed: true
      });

      await supabase.from('squad_invites').update({ used_count: (existingMembers.length + 1) }).eq('invite_code', inviteCode);
      await supabase.from('squad_registrations').update({ current_members: (members?.length || 0) + 1 }).eq('id', squadId);

      // Clear member session from localStorage
      if (inviteCode) {
        localStorage.removeItem(STORAGE_KEYS.MEMBER_SESSION(inviteCode));
        localStorage.removeItem(STORAGE_KEYS.MEMBER_ROLE);
        localStorage.removeItem(STORAGE_KEYS.MEMBER_INFO);
        localStorage.removeItem(STORAGE_KEYS.MEMBER_ASSESSMENT);
      }

      toast({ title: 'Registration Complete!', description: `You have joined ${squadInfo.squadName}.` });
      navigate('/forge/application-submitted');
    } catch (error) {
      console.error('Error registering member:', error);
      toast({ title: 'Error', description: 'Failed to register.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const submitFinalApplication = async () => {
    if (!leadAssessmentCompleted) {
      setError('Complete your assessment first.');
      return;
    }

    const maxTeamMembers = getMaxTeamMembersFromTeamSize(squadInfo.teamSize);
    
    if (existingMembers.length < 2) {
      setError(`Need at least 3 members total (including you). Currently: ${existingMembers.length + 1}`);
      return;
    }

    const allCompleted = existingMembers.every(m => m.assessment_completed);
    if (!allCompleted) {
      setError(`${existingMembers.filter(m => !m.assessment_completed).length} member(s) still need to complete assessments.`);
      return;
    }

    setLoading(true);
    try {
      await supabase.from('squad_registrations').update({ 
        status: 'submitted',
        submitted_at: new Date().toISOString()
      }).eq('id', squadId);

      localStorage.removeItem(STORAGE_KEYS.LEAD_SQUAD_ID);
      localStorage.removeItem(STORAGE_KEYS.LEAD_SQUAD_INFO);
      localStorage.removeItem(STORAGE_KEYS.LEAD_INTERESTS);
      localStorage.removeItem(STORAGE_KEYS.LEAD_ASSESSMENT);
      localStorage.removeItem(STORAGE_KEYS.LEAD_INVITE_LINK);
      localStorage.removeItem(STORAGE_KEYS.CUSTOM_ROLES);

      toast({ title: 'Application Submitted!', description: 'Your squad application has been submitted for review.' });
      navigate('/forge/application-submitted');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({ title: 'Error', description: 'Failed to submit application.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: 'Copied!', description: 'Invite link copied to clipboard' });
  };

  const getRoleLabel = (role: string) => {
    const allRoles = getAllRoles();
    const foundRole = allRoles.find(r => r.id === role);
    return foundRole?.name || role;
  };

  // Check if business interests are complete
  const isBusinessInterestsComplete = () => {
    return businessInterests.primarySectors.length > 0 && 
           businessInterests.problemCategories.length > 0;
  };

  const currentMaxMembers = getMaxMembersFromTeamSize(squadInfo.teamSize);
  const currentMaxTeamMembers = getMaxTeamMembersFromTeamSize(squadInfo.teamSize);

  // Loading state
  if (loading && registrationMode === 'member' && inviteValid === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Invalid invite
  if (inviteValid === false && registrationMode === 'member') {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-md mx-auto px-4">
          <Card>
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <CardTitle>Invalid Invite Link</CardTitle>
              <CardDescription>This invite link is invalid, expired, or has been used too many times.</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate('/forge')}>Return to Forge</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Invite Step (Lead)
  if (currentStep === 'invite' && registrationMode === 'lead') {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl">Squad Created!</CardTitle>
              <CardDescription>Your squad "{squadInfo.squadName}" has been created. Share the invite link with your team members.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <Label className="text-sm font-medium mb-2 block">Invite Link (Share with up to {currentMaxTeamMembers} members)</Label>
                <div className="flex gap-2">
                  <Input value={inviteLink} readOnly className="font-mono text-sm" />
                  <Button onClick={copyInviteLink} variant="outline">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Share this link with up to {currentMaxTeamMembers} team members. Link expires in 7 days.</p>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium">What would you like to do next?</h3>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <Button onClick={() => setCurrentStep('lead-assessment')} className="flex flex-col h-auto py-4 gap-2 bg-blue-600 hover:bg-blue-700">
                    <UserCheck className="h-6 w-6" />
                    <div><p className="font-medium text-sm">Complete Assessment</p><p className="text-xs text-blue-100">Take your assessment</p></div>
                  </Button>
                  <Button onClick={() => setCurrentStep('review')} variant="outline" className="flex flex-col h-auto py-4 gap-2">
                    <Users className="h-6 w-6" />
                    <div><p className="font-medium text-sm">Squad Dashboard</p><p className="text-xs text-muted-foreground">Monitor progress</p></div>
                  </Button>
                  <Button onClick={() => navigate('/forge/dashboard')} variant="outline" className="flex flex-col h-auto py-4 gap-2">
                    <Home className="h-6 w-6" />
                    <div><p className="font-medium text-sm">Exit</p><p className="text-xs text-muted-foreground">Save & exit</p></div>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Lead Assessment Step (All questions on one page)
  if (currentStep === 'lead-assessment' && registrationMode === 'lead') {
    const allBaseAnswered = baseQuestions.every(q => leadAssessment[q.id] && leadAssessment[q.id].trim() !== '');
    const roleQuestions = getRoleSpecificQuestions('lead');
    const allRoleAnswered = roleQuestions.every(q => leadAssessment[q.id] && leadAssessment[q.id].trim() !== '');
    const allAnswered = allBaseAnswered && allRoleAnswered;
    
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <CardTitle className="text-2xl">Your Assessment</CardTitle>
                  <CardDescription>Complete this assessment for "{squadInfo.squadName}"</CardDescription>
                </div>
                <Badge className="bg-primary/10 text-primary border-primary/20">Step 4 of 6</Badge>
              </div>
              <Progress value={progress} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/30 rounded-lg border">
                <h3 className="font-medium mb-3">Your Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Name</p><p className="font-medium">{squadInfo.leadName}</p></div>
                  <div><p className="text-muted-foreground">Email</p><p className="font-medium">{squadInfo.leadEmail}</p></div>
                  <div><p className="text-muted-foreground">University</p><p className="font-medium">{squadInfo.university}</p></div>
                  <div><p className="text-muted-foreground">Role</p><p className="font-medium">Project Lead (Squad Leader)</p></div>
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-semibold">Base Questions</h3>
                {baseQuestions.map((q, idx) => (
                  <div key={q.id} className="space-y-2">
                    <Label className="text-sm flex items-start gap-2">
                      <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{idx + 1}</span>
                      <span>{q.text}</span>
                    </Label>
                    <Textarea 
                      placeholder="Your answer..." 
                      value={leadAssessment[q.id] || ''} 
                      onChange={(e) => setLeadAssessment({...leadAssessment, [q.id]: e.target.value})} 
                      className="min-h-[100px] mt-1" 
                    />
                  </div>
                ))}

                <Separator className="my-4" />
                
                <h3 className="text-lg font-semibold">Leadership Questions</h3>
                {roleQuestions.map((q, idx) => (
                  <div key={q.id} className="space-y-2">
                    <Label className="text-sm flex items-start gap-2">
                      <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{idx + 6}</span>
                      <span>{q.text}</span>
                    </Label>
                    <Textarea 
                      placeholder="Your answer..." 
                      value={leadAssessment[q.id] || ''} 
                      onChange={(e) => setLeadAssessment({...leadAssessment, [q.id]: e.target.value})} 
                      className="min-h-[100px] mt-1" 
                    />
                  </div>
                ))}
              </div>

              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setCurrentStep('invite')}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
                <Button onClick={submitLeadAssessment} disabled={!allAnswered || loading} className="bg-green-600 hover:bg-green-700">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Save & Continue'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Member Role Selection Step (with university display)
  if (currentStep === 'member-role-select' && registrationMode === 'member') {
    const allRoles = getAllRoles();
    const roleOptions = allRoles.map(role => ({
      id: role.id,
      name: role.name,
      value: role.id
    }));
    
    const canProceed = memberInfo.fullName && memberInfo.email && memberInfo.role && memberInfo.yearOfStudy && memberInfo.course;
    
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <CardTitle className="text-2xl">Join {squadInfo.squadName}</CardTitle>
                  <CardDescription>Tell us about yourself and choose your role</CardDescription>
                </div>
                <Badge className="bg-primary/10 text-primary border-primary/20">Step 1 of 2</Badge>
              </div>
              <Progress value={progress} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
                <h3 className="font-medium">Your Information</h3>
                
                {/* Squad University - Display Only */}
                <div className="bg-blue-50 p-3 rounded-md mb-2">
                  <p className="text-sm text-blue-800">
                    <strong>Squad University:</strong> {squadInfo.university}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    This squad is associated with {squadInfo.university}. Please verify your affiliation.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input 
                      placeholder="Your full name" 
                      value={memberInfo.fullName} 
                      onChange={(e) => setMemberInfo({...memberInfo, fullName: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      type="email" 
                      placeholder="your.email@example.com" 
                      value={memberInfo.email} 
                      onChange={(e) => setMemberInfo({...memberInfo, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Phone (Optional)</Label>
                    <Input 
                      placeholder="+254 700 000 000" 
                      value={memberInfo.phone} 
                      onChange={(e) => setMemberInfo({...memberInfo, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Role *</Label>
                    <Select value={memberInfo.role} onValueChange={(v) => setMemberInfo({...memberInfo, role: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roleOptions.map(role => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {memberInfo.role === 'market_analyst' && 'Focus on market research, customer discovery, and competitor analysis'}
                      {memberInfo.role === 'product_lead' && 'Focus on product development, feature prioritization, and user experience'}
                      {memberInfo.role === 'finance_lead' && 'Focus on financial planning, budgeting, and business modeling'}
                      {memberInfo.role && !['market_analyst', 'product_lead', 'finance_lead'].includes(memberInfo.role) && 'Custom role - contribute your unique skills to the squad'}
                    </p>
                  </div>
                  <div>
                    <Label>Year of Study *</Label>
                    <Input 
                      placeholder="e.g., 3rd Year" 
                      value={memberInfo.yearOfStudy} 
                      onChange={(e) => setMemberInfo({...memberInfo, yearOfStudy: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Course *</Label>
                    <Input 
                      placeholder="e.g., Computer Science" 
                      value={memberInfo.course} 
                      onChange={(e) => setMemberInfo({...memberInfo, course: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {memberInfo.role && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>About the {getRoleLabel(memberInfo.role)} role:</strong><br />
                    {memberInfo.role === 'market_analyst' && 'You will conduct market research, analyze competitors, interview customers, and validate problem-solution fit.'}
                    {memberInfo.role === 'product_lead' && 'You will define product requirements, prioritize features, design user experiences, and oversee development.'}
                    {memberInfo.role === 'finance_lead' && 'You will build financial models, manage budgets, analyze unit economics, and prepare for fundraising.'}
                    {memberInfo.role && !['market_analyst', 'product_lead', 'finance_lead'].includes(memberInfo.role) && `As ${memberInfo.role}, you will bring your unique expertise to help the squad succeed.`}
                  </p>
                </div>
              )}

              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => navigate('/forge')}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button 
                  onClick={handleMemberInfoSubmit}
                  disabled={!canProceed}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Continue to Assessment
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Member Assessment Step (2 pages - 5 questions each)
  if (currentStep === 'member-assessment' && registrationMode === 'member') {
    const currentQuestions = assessmentPage === 1 ? baseQuestions : getRoleSpecificQuestions(memberInfo.role);
    const pageTitle = assessmentPage === 1 ? 'Base Questions' : `${getRoleLabel(memberInfo.role)} Questions`;
    const totalPages = 2;
    
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <CardTitle className="text-2xl">Your Assessment</CardTitle>
                  <CardDescription>
                    {getRoleLabel(memberInfo.role)} • Page {assessmentPage} of {totalPages} • {pageTitle}
                  </CardDescription>
                </div>
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  {assessmentPage === 1 ? 'Part 1 of 2' : 'Part 2 of 2'}
                </Badge>
              </div>
              <Progress value={assessmentPage === 1 ? 50 : 100} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/30 rounded-lg border">
                <h3 className="font-medium mb-3">Your Role: {getRoleLabel(memberInfo.role)}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><p className="text-muted-foreground">Name</p><p className="font-medium">{memberInfo.fullName}</p></div>
                  <div><p className="text-muted-foreground">Email</p><p className="font-medium">{memberInfo.email}</p></div>
                  <div><p className="text-muted-foreground">University</p><p className="font-medium">{squadInfo.university}</p></div>
                  <div><p className="text-muted-foreground">Course</p><p className="font-medium">{memberInfo.course}</p></div>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-primary">
                  {assessmentPage === 1 
                    ? 'These questions help us understand your motivation and background.' 
                    : `These questions are specific to the ${getRoleLabel(memberInfo.role)} role. Answer honestly to help us understand your fit for this role.`}
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="text-lg font-semibold">{pageTitle} ({currentQuestions.length} questions)</h3>
                {currentQuestions.map((q, idx) => (
                  <div key={q.id} className="space-y-2">
                    <Label className="text-sm flex items-start gap-2">
                      <span className="bg-primary/20 text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">
                        {assessmentPage === 1 ? idx + 1 : idx + 6}
                      </span>
                      <span>{q.text}</span>
                    </Label>
                    <Textarea
                      placeholder="Your answer..."
                      value={memberAssessment[q.id] || ''}
                      onChange={(e) => setMemberAssessment({...memberAssessment, [q.id]: e.target.value})}
                      className="min-h-[120px] mt-1"
                    />
                  </div>
                ))}
              </div>

              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex justify-between pt-4 border-t">
                {assessmentPage === 1 ? (
                  <Button variant="outline" onClick={() => setCurrentStep('member-role-select')}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back to Info
                  </Button>
                ) : (
                  <Button variant="outline" onClick={() => setAssessmentPage(1)}>
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Back to Base Questions
                  </Button>
                )}
                
                {assessmentPage === 1 ? (
                  <Button 
                    onClick={handleNextPage}
                    disabled={!isPageComplete()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Continue to Role Questions
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmitAssessment}
                    disabled={!isPageComplete() || loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Submit & Join Squad'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Review Step - Lead sees progress
  if (currentStep === 'review' && registrationMode === 'lead') {
    const leadMember = existingMembers.find(m => m.role === 'lead');
    const leadCompleted = leadMember?.assessment_completed || leadAssessmentCompleted;
    const teamMembersCount = existingMembers.filter(m => m.role !== 'lead').length;
    const completedCount = existingMembers.filter(m => m.assessment_completed).length;
    const maxTeamMembers = getMaxTeamMembersFromTeamSize(squadInfo.teamSize);
    const canSubmit = leadCompleted && teamMembersCount >= 2 && completedCount === existingMembers.length;
    
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div><CardTitle className="text-2xl">Squad Dashboard</CardTitle><CardDescription>Track your squad's progress and submit when ready</CardDescription></div>
                <Badge className="bg-primary/10 text-primary border-primary/20">Step 5 of 6</Badge>
              </div>
              <Progress value={progress} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted/30 rounded-lg"><h3 className="font-medium mb-2">{squadInfo.squadName}</h3><p className="text-sm text-muted-foreground">{squadInfo.university}</p></div>

              {/* Business Interests Summary */}
              <div className="bg-primary/5 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-2 flex items-center gap-2"><Target className="h-4 w-4" />Squad Interests</h4>
                <div className="flex flex-wrap gap-2 mb-2">
                  {businessInterests.primarySectors.slice(0, 3).map(s => (<Badge key={s} className="bg-blue-100 text-blue-700">{s.replace('_', ' ')}</Badge>))}
                  {businessInterests.problemCategories.slice(0, 3).map(c => (<Badge key={c} variant="secondary">{c.replace(/_/g, ' ')}</Badge>))}
                </div>
                <p className="text-xs text-muted-foreground">These interests will help us match your squad with relevant problems.</p>
              </div>

              {/* Roles Section */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Squad Roles ({teamMembersCount} / {maxTeamMembers} filled)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {getAllRoles().map(role => {
                    const isFilled = existingMembers.some(m => m.role === role.id);
                    return (
                      <Badge 
                        key={role.id} 
                        className={isFilled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}
                      >
                        {role.name}
                        {isFilled ? ' ✓' : ' (open)'}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center"><h3 className="font-medium flex items-center gap-2"><Users className="h-4 w-4" />Team Members ({existingMembers.length + 1}/{currentMaxMembers})</h3><Button variant="ghost" size="sm" onClick={copyInviteLink} className="text-xs"><Copy className="h-3 w-3 mr-1" />Copy Invite Link</Button></div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-primary/20">
                    <div>
                      <p className="font-medium">{squadInfo.leadName} (You)</p>
                      <p className="text-xs text-muted-foreground">{squadInfo.leadEmail}</p>
                      <p className="text-xs text-primary mt-1">Project Lead (Squad Leader)</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-primary/10 text-primary">Project Lead</Badge>
                      {leadCompleted ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge> : <Button variant="outline" size="sm" onClick={() => setCurrentStep('lead-assessment')}>Complete Assessment</Button>}
                    </div>
                  </div>
                  {existingMembers.filter(m => m.role !== 'lead').map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">{member.full_name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                        <p className="text-xs text-primary mt-1">{getRoleLabel(member.role)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-primary/10 text-primary">{getRoleLabel(member.role)}</Badge>
                        {member.assessment_completed ? <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge> : <Badge className="bg-yellow-100 text-yellow-700"><AlertCircle className="h-3 w-3 mr-1" />Pending</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Progress Summary</h4>
                <div className="space-y-1 text-sm">
                  <p className="text-blue-700">✓ Your assessment: {leadCompleted ? 'Completed' : 'Pending'}</p>
                  <p className="text-blue-700">📋 Team members joined: {teamMembersCount}/{maxTeamMembers}</p>
                  <p className="text-blue-700">📝 Assessments completed: {completedCount}/{existingMembers.length}</p>
                  {!leadCompleted && <p className="text-yellow-700 mt-2">⚠️ Please complete your assessment first.</p>}
                  {leadCompleted && teamMembersCount >= 2 && completedCount === existingMembers.length && <p className="text-green-700 font-medium mt-2">✓ All members have completed their assessments! Ready to submit.</p>}
                  {leadCompleted && teamMembersCount < 2 && <p className="text-yellow-700 mt-2">⚠️ Need at least 2 more members (3 total including you) to submit. Share the invite link!</p>}
                  {leadCompleted && teamMembersCount >= 2 && completedCount < existingMembers.length && <p className="text-yellow-700 mt-2">⚠️ Waiting for {existingMembers.length - completedCount} member(s) to complete their assessments.</p>}
                </div>
              </div>

              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setCurrentStep('invite')}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
                <Button onClick={submitFinalApplication} disabled={!canSubmit || loading} className="bg-green-600 hover:bg-green-700">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Submit Application'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Business Interests Step
  if (currentStep === 'business-interests' && registrationMode === 'lead') {
    const isComplete = isBusinessInterestsComplete();
    
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <CardTitle className="text-2xl">Your Squad's Interests</CardTitle>
                  <CardDescription>
                    Tell us what types of problems your squad wants to solve. This helps us match you with relevant opportunities.
                  </CardDescription>
                </div>
                <Badge className="bg-primary/10 text-primary border-primary/20">Step 2 of 6</Badge>
              </div>
              <Progress value={progress} className="h-2" />
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Template Selector */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4" />Quick Start Templates</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button variant={selectedTemplate === 'climate_tech' ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => applyTemplate('climate_tech')}>🌱 Climate Tech</Button>
                  <Button variant={selectedTemplate === 'agritech' ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => applyTemplate('agritech')}>🌾 AgriTech</Button>
                  <Button variant={selectedTemplate === 'digital_transformation' ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => applyTemplate('digital_transformation')}>💻 Digital Transformation</Button>
                  <Button variant={selectedTemplate === 'manufacturing_excellence' ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => applyTemplate('manufacturing_excellence')}>🏭 Manufacturing Excellence</Button>
                  <Button variant={selectedTemplate === 'healthtech' ? 'default' : 'outline'} size="sm" className="justify-start" onClick={() => applyTemplate('healthtech')}>🏥 HealthTech</Button>
                </div>
              </div>

              {/* Primary Sectors */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Primary Industry Sectors *</Label>
                <p className="text-sm text-muted-foreground">Select the industries your squad is most interested in</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {industrySectorOptions.map((sector) => (
                    <div key={sector.id} className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition ${businessInterests.primarySectors.includes(sector.id) ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}`} onClick={() => {
                      if (businessInterests.primarySectors.includes(sector.id)) {
                        setBusinessInterests({...businessInterests, primarySectors: businessInterests.primarySectors.filter(s => s !== sector.id)});
                      } else {
                        setBusinessInterests({...businessInterests, primarySectors: [...businessInterests.primarySectors, sector.id]});
                      }
                    }}>
                      <Checkbox checked={businessInterests.primarySectors.includes(sector.id)} />
                      <div className="flex-1">
                        <Label className="font-medium cursor-pointer">{sector.label}</Label>
                        <p className="text-xs text-muted-foreground">{sector.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sub-Sectors */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Preferred Sub-Sectors</Label>
                <p className="text-sm text-muted-foreground">Select specific areas within your chosen sectors</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {subSectorOptions.filter(ss => businessInterests.primarySectors.includes(ss.sector)).map((sub) => (
                    <div key={sub.id} className="flex items-center space-x-2">
                      <Checkbox id={sub.id} checked={businessInterests.preferredSubSectors.includes(sub.id)} onCheckedChange={(checked) => {
                        if (checked) {
                          setBusinessInterests({...businessInterests, preferredSubSectors: [...businessInterests.preferredSubSectors, sub.id]});
                        } else {
                          setBusinessInterests({...businessInterests, preferredSubSectors: businessInterests.preferredSubSectors.filter(s => s !== sub.id)});
                        }
                      }} />
                      <Label htmlFor={sub.id} className="text-sm cursor-pointer">{sub.label}</Label>
                    </div>
                  ))}
                  {businessInterests.primarySectors.length === 0 && <p className="text-sm text-muted-foreground col-span-full">Select primary sectors first</p>}
                </div>
              </div>

              {/* Problem Categories */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Problem Categories You Want to Solve *</Label>
                <p className="text-sm text-muted-foreground">What types of business problems interest your squad?</p>
                {['Operational Efficiency', 'Cost Reduction', 'Revenue Growth', 'Technology & Digital', 'Sustainability & ESG', 'Financial & Business'].map(group => (
                  <div key={group} className="mt-3">
                    <h4 className="text-sm font-medium mb-2">{group}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {problemCategoryOptions.filter(pc => pc.group === group).map((cat) => (
                        <div key={cat.id} className="flex items-center space-x-2">
                          <Checkbox id={cat.id} checked={businessInterests.problemCategories.includes(cat.id)} onCheckedChange={(checked) => {
                            if (checked) {
                              setBusinessInterests({...businessInterests, problemCategories: [...businessInterests.problemCategories, cat.id]});
                            } else {
                              setBusinessInterests({...businessInterests, problemCategories: businessInterests.problemCategories.filter(c => c !== cat.id)});
                            }
                          }} />
                          <Label htmlFor={cat.id} className="text-sm cursor-pointer">{cat.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Technical Areas */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Technical Skills</Label>
                <p className="text-sm text-muted-foreground">Select your squad's technical strengths</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {technicalAreaOptions.map((area) => (
                    <div key={area.id} className="flex items-center space-x-2">
                      <Checkbox id={area.id} checked={businessInterests.technicalAreas.includes(area.id)} onCheckedChange={(checked) => {
                        if (checked) {
                          setBusinessInterests({...businessInterests, technicalAreas: [...businessInterests.technicalAreas, area.id]});
                        } else {
                          setBusinessInterests({...businessInterests, technicalAreas: businessInterests.technicalAreas.filter(t => t !== area.id)});
                        }
                      }} />
                      <Label htmlFor={area.id} className="text-sm cursor-pointer">{area.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Impact Areas */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Impact Areas</Label>
                <p className="text-sm text-muted-foreground">What social or environmental impact matters to your squad?</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {impactAreaOptions.map((area) => (
                    <div key={area.id} className="flex items-center space-x-2">
                      <Checkbox id={area.id} checked={businessInterests.impactAreas.includes(area.id)} onCheckedChange={(checked) => {
                        if (checked) {
                          setBusinessInterests({...businessInterests, impactAreas: [...businessInterests.impactAreas, area.id]});
                        } else {
                          setBusinessInterests({...businessInterests, impactAreas: businessInterests.impactAreas.filter(i => i !== area.id)});
                        }
                      }} />
                      <Label htmlFor={area.id} className="text-sm cursor-pointer">{area.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Company Stage Preferences */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Company Stage Preferences</Label>
                <p className="text-sm text-muted-foreground">What types of companies do you want to work with?</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {companyStageOptions.map((stage) => (
                    <div key={stage.id} className="flex items-center space-x-2">
                      <Checkbox id={stage.id} checked={businessInterests.companyStagePreferences.includes(stage.id)} onCheckedChange={(checked) => {
                        if (checked) {
                          setBusinessInterests({...businessInterests, companyStagePreferences: [...businessInterests.companyStagePreferences, stage.id]});
                        } else {
                          setBusinessInterests({...businessInterests, companyStagePreferences: businessInterests.companyStagePreferences.filter(c => c !== stage.id)});
                        }
                      }} />
                      <Label htmlFor={stage.id} className="text-sm cursor-pointer">{stage.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Engagement Preferences */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">How does your squad prefer to work?</Label>
                <p className="text-sm text-muted-foreground">Select your preferred engagement styles</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {engagementOptions.map((eng) => (
                    <div key={eng.id} className="flex items-center space-x-2">
                      <Checkbox id={eng.id} checked={businessInterests.engagementPreferences.includes(eng.id)} onCheckedChange={(checked) => {
                        if (checked) {
                          setBusinessInterests({...businessInterests, engagementPreferences: [...businessInterests.engagementPreferences, eng.id]});
                        } else {
                          setBusinessInterests({...businessInterests, engagementPreferences: businessInterests.engagementPreferences.filter(e => e !== eng.id)});
                        }
                      }} />
                      <Label htmlFor={eng.id} className="text-sm cursor-pointer">{eng.label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Looking For */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">What is your squad looking for?</Label>
                <p className="text-sm text-muted-foreground">Select what you hope to gain from The Forge</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {['Hands-on problem solving', 'Market research and analysis', 'Product development', 'Business model creation', 'Financial modeling', 'Customer discovery', 'Pitching and presentation', 'Technical implementation'].map(option => (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox id={option.replace(/\s/g, '_')} checked={lookingFor.includes(option)} onCheckedChange={(checked) => {
                        if (checked) setLookingFor([...lookingFor, option]);
                        else setLookingFor(lookingFor.filter(l => l !== option));
                      }} />
                      <Label htmlFor={option.replace(/\s/g, '_')} className="text-sm cursor-pointer">{option}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary of Selections */}
              {(businessInterests.primarySectors.length > 0 || businessInterests.problemCategories.length > 0) && (
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="text-sm font-medium mb-2">Your Squad's Profile:</p>
                  <div className="flex flex-wrap gap-2">
                    {businessInterests.primarySectors.slice(0, 3).map(s => (<Badge key={s} className="bg-green-100 text-green-700">{industrySectorOptions.find(i => i.id === s)?.label}</Badge>))}
                    {businessInterests.problemCategories.slice(0, 3).map(c => (<Badge key={c} variant="secondary">{problemCategoryOptions.find(p => p.id === c)?.label}</Badge>))}
                    {businessInterests.impactAreas.slice(0, 2).map(i => (<Badge key={i} variant="outline" className="bg-blue-50">{impactAreaOptions.find(ia => ia.id === i)?.label}</Badge>))}
                  </div>
                </div>
              )}

              {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setCurrentStep('squad-info')}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
                <Button onClick={() => setCurrentStep('invite')} disabled={!isComplete} className="bg-blue-600 hover:bg-blue-700">Continue to Invite</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Squad Info Step (Lead creates squad with custom roles)
  if (currentStep === 'squad-info' && registrationMode === 'lead') {
    const isComplete = squadInfo.squadName.trim() !== '' && 
                       squadInfo.university.trim() !== '' && 
                       squadInfo.leadName.trim() !== '' && 
                       squadInfo.leadEmail.trim() !== '';
    
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="max-w-3xl mx-auto px-4">
          <Link to="/forge" className="text-muted-foreground hover:text-foreground mb-6 inline-flex items-center">← Back to Forge</Link>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div><CardTitle className="text-2xl">Create Your Squad</CardTitle><CardDescription>Start by creating your squad - you'll get an invite link to share with team members</CardDescription></div>
                <Badge className="bg-primary/10 text-primary border-primary/20">Step 1 of 6</Badge>
              </div>
              <Progress value={progress} className="h-2" />
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                  <p className="text-sm text-primary flex items-center">
                    <Sparkles className="h-4 w-4 mr-2" />
                    You're creating a squad. After registration, you'll get an invite link to share with your team.
                  </p>
                </div>

                {/* Basic Squad Info */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="squadName">Squad Name *</Label>
                    <Input
                      id="squadName"
                      placeholder="e.g., Alpha Innovators"
                      value={squadInfo.squadName}
                      onChange={(e) => setSquadInfo({...squadInfo, squadName: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="university">University/Institution *</Label>
                    <Input
                      id="university"
                      placeholder="e.g., University of Nairobi"
                      value={squadInfo.university}
                      onChange={(e) => setSquadInfo({...squadInfo, university: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Team Size</Label>
                      <Select value={squadInfo.teamSize} onValueChange={(v: TeamSize) => setSquadInfo({...squadInfo, teamSize: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {teamSizeOptions.map(s => (<SelectItem key={s} value={s}>{s} members (including you)</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {squadInfo.teamSize === '2-3' ? 'You + 2 team members' : 
                         squadInfo.teamSize === '4-5' ? 'You + 4 team members' : 
                         'You + 7 team members'}
                      </p>
                    </div>
                    <div>
                      <Label>Experience Level</Label>
                      <Select value={squadInfo.experienceLevel} onValueChange={(v: ExperienceLevel) => setSquadInfo({...squadInfo, experienceLevel: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {experienceLevelOptions.map(l => (<SelectItem key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Custom Roles Section */}
                <div className="border-t border-border pt-6">
                  <h3 className="text-foreground font-medium mb-4 flex items-center">
                    <Users className="h-5 w-5 text-primary mr-2" />
                    Squad Roles
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Team members will choose from these roles when joining. The following 3 default roles are required:
                  </p>

                  {/* Default Roles (non-removable) */}
                  <div className="mb-4">
                    <Label className="text-sm font-semibold">Default Roles (Required)</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {DEFAULT_ROLES.map(role => (
                        <Badge key={role.id} className="bg-blue-100 text-blue-700 px-3 py-1">
                          {role.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Custom Roles */}
                  {customRoles.length > 0 && (
                    <div className="mb-4">
                      <Label className="text-sm font-semibold">Custom Roles</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {customRoles.map(role => (
                          <Badge key={role.id} className="bg-green-100 text-green-700 px-3 py-1 flex items-center gap-1">
                            {role.name}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 hover:bg-transparent"
                              onClick={() => removeCustomRole(role.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Custom Role */}
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Add custom role (e.g., UX Designer)"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && addCustomRole()}
                    />
                    <Button onClick={addCustomRole} variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Add Role
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Team members will choose from these {getAllRoles().length} roles when joining.
                  </p>
                </div>

                {/* Lead Information */}
                <div className="border-t border-border pt-6">
                  <h3 className="text-foreground font-medium mb-4 flex items-center">
                    <Award className="h-5 w-5 text-primary mr-2" />
                    Your Information (Squad Lead)
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="leadName">Full Name *</Label>
                      <Input
                        id="leadName"
                        placeholder="John Doe"
                        value={squadInfo.leadName}
                        onChange={(e) => setSquadInfo({...squadInfo, leadName: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="leadEmail">Email *</Label>
                      <Input
                        id="leadEmail"
                        type="email"
                        placeholder="john@example.com"
                        value={squadInfo.leadEmail}
                        onChange={(e) => setSquadInfo({...squadInfo, leadEmail: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="leadPhone">Phone Number (Optional)</Label>
                      <Input
                        id="leadPhone"
                        placeholder="+254 700 000 000"
                        value={squadInfo.leadPhone}
                        onChange={(e) => setSquadInfo({...squadInfo, leadPhone: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" onClick={() => navigate('/forge')}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Cancel
                  </Button>
                  <Button 
                    onClick={createSquad}
                    disabled={!isComplete || loading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Squad'
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return null;
}